import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    ArrowLeft, ArrowRight, Loader2, Save, Check, Plus, 
    Target, AlertCircle, Sparkles, Info
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIAS = [
    { id: 'procrastinacion', label: 'Procrastinación' },
    { id: 'cansancio', label: 'Cansancio' },
    { id: 'falta_motivacion', label: 'Falta de motivación' },
    { id: 'ansiedad', label: 'Ansiedad' },
    { id: 'confusion', label: 'Confusión' },
    { id: 'perfeccionismo', label: 'Perfeccionismo' },
    { id: 'miedo_fracaso', label: 'Miedo al fracaso' },
    { id: 'distraccion', label: 'Distracción' },
    { id: 'sobrecarga', label: 'Sobrecarga' },
    { id: 'otros', label: 'Otros' }
];

const CERTEZA_NIVELES = [
    { value: 'muy_seguro', label: 'Muy seguro/a' },
    { value: 'bastante_seguro', label: 'Bastante seguro/a' },
    { value: 'algo_seguro', label: 'Algo seguro/a' },
    { value: 'poco_seguro', label: 'Poco seguro/a' },
    { value: 'nada_seguro', label: 'Nada seguro/a' }
];

const emptySituacion = {
    situacion: '',
    etiqueta_automatica: '',
    categoria_real: [],
    nivel_certeza: ''
};

export default function Modulo1Diferencial() {
    const navigate = useNavigate();
    const [situaciones, setSituaciones] = useState([
        { ...emptySituacion },
        { ...emptySituacion },
        { ...emptySituacion }
    ]);
    const [currentCard, setCurrentCard] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    // Load draft on mount
    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/diferencial_inicial`, {
                    withCredentials: true
                });
                if (data.data?.situaciones) {
                    setSituaciones(data.data.situaciones.map(s => ({
                        ...emptySituacion,
                        ...s,
                        categoria_real: Array.isArray(s.categoria_real) ? s.categoria_real : [],
                    })));
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    // Auto-save draft
    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'diferencial_inicial', data: { situaciones } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [situaciones]);

    // Debounced auto-save
    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [situaciones, saveDraft, loadingDraft, readOnly]);

    const updateSituacion = (index, field, value) => {
        setSituaciones(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const toggleCategoria = (index, categoriaId) => {
        setSituaciones(prev => prev.map((item, i) => {
            if (i !== index) return item;
            const current = Array.isArray(item.categoria_real) ? item.categoria_real : [];
            return current.includes(categoriaId)
                ? { ...item, categoria_real: current.filter(c => c !== categoriaId) }
                : { ...item, categoria_real: [...current, categoriaId] };
        }));
    };

    const isSituacionComplete = (sit) => {
        if (!sit) return false;
        return sit.situacion?.trim().length > 0 &&
               sit.etiqueta_automatica?.trim().length > 0 &&
               (Array.isArray(sit.categoria_real) ? sit.categoria_real.length : 0) > 0 &&
               sit.nivel_certeza;
    };

    const completedCount = situaciones.filter(isSituacionComplete).length;
    
    // Check for at least 2 distinct categories
    const allCategories = new Set();
    situaciones.forEach(sit => {
        if (Array.isArray(sit.categoria_real)) {
            sit.categoria_real.forEach(c => allCategories.add(c));
        }
    });
    const hasEnoughCategories = allCategories.size >= 2;

    const canSubmit = !readOnly && (completedCount >= 3 && hasEnoughCategories);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todas las situaciones y asegúrate de tener al menos 2 categorías distintas');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/diferencial/submit`,
                { situaciones },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado exitosamente');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar el módulo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#425A65]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl senda-card animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-[#7F9C8B] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="senda-heading-2 mb-4">¡Módulo completado!</h2>
                        
                        {aiInsight && (
                            <div className="senda-ai-panel text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#A29DB0] shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#A29DB0] uppercase mb-2">Reflexión para ti</p>
                                        <p className="text-[#1F2933] italic">"{aiInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <p className="senda-body mb-6">
                            Has dado un paso importante en tu proceso de autoconocimiento. 
                            Tu mentor-psicóloga revisará tus respuestas y te proporcionará retroalimentación.
                        </p>
                        
                        <div className="mb-6"><ModuleFeedback moduleId="diferencial_inicial" chapter={1} /></div>
                        <Link to="/usuario">
                            <Button className="senda-btn-primary" data-testid="back-to-dashboard-btn">
                                Volver al inicio
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const currentSit = situaciones[currentCard] || emptySituacion;
    const currentCategorias = Array.isArray(currentSit.categoria_real) ? currentSit.categoria_real : [];

    return (
        <div className="min-h-screen bg-[#FBFBF9]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#425A65]" data-testid="back-to-usuario">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Diferencial inicial de procrastinación</h1>
                                <p className="text-sm text-[#616E7C]">Situación {currentCard + 1} de 3</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#9AA5B1] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Guardando...
                                </span>
                            )}
                            <Badge className={completedCount >= 3 ? 'senda-status-success' : 'bg-[#EAE8E3] text-[#616E7C]'}>
                                {completedCount}/3 completas
                            </Badge>
                        </div>
                    </div>
                </div>
            </header>

            {/* Progress indicators */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex gap-2">
                    {[0, 1, 2].map((idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentCard(idx)}
                            className={`flex-1 h-2 rounded-full transition-all ${
                                idx === currentCard 
                                    ? 'bg-[#425A65]' 
                                    : isSituacionComplete(situaciones[idx])
                                        ? 'bg-[#7F9C8B]'
                                        : 'bg-[#EAE8E3]'
                            }`}
                            data-testid={`progress-indicator-${idx}`}
                        />
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="senda-module-container animate-fade-in-up">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#425A65] rounded-xl flex items-center justify-center">
                                <Target className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#425A65]">Situación {currentCard + 1}</CardTitle>
                                <p className="text-sm text-[#616E7C]">Reflexiona sobre un momento reciente</p>
                            </div>
                        </div>
                        
                        {/* Info box */}
                        <div className="p-4 bg-[#FBFBF9] rounded-lg border border-[#EAE8E3] flex items-start gap-3">
                            <Info className="h-5 w-5 text-[#A29DB0] shrink-0 mt-0.5" />
                            <div className="space-y-2">
                                <p className="text-sm text-[#616E7C]">
                                    Piensa en una situación donde sentiste que estabas procrastinando. 
                                    El objetivo es examinar si realmente era procrastinación o si había otra causa subyacente.
                                </p>
                                <p className="text-xs text-[#5FAF9D] font-medium" data-testid="instruccion-3-situaciones">
                                    Esta actividad se realiza 3 veces, una por cada situación, como parte del ejercicio.
                                </p>
                            </div>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                        {/* Situación */}
                        <div className="space-y-2">
                            <Label className="senda-label">Describe la situación</Label>
                            <Textarea
                                value={currentSit.situacion}
                                onChange={(e) => updateSituacion(currentCard, 'situacion', e.target.value)}
                                placeholder="Ej: Ayer por la tarde tenía que terminar un informe pero en lugar de eso me puse a ver videos..."
                                className="senda-input min-h-[120px]"
                                data-testid={`situacion-input-${currentCard}`}
                            />
                            <p className="text-xs text-[#9AA5B1]">Sé lo más específico/a posible.</p>
                        </div>

                        {/* Etiqueta automática */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué te dijiste automáticamente?</Label>
                            <Input
                                value={currentSit.etiqueta_automatica}
                                onChange={(e) => updateSituacion(currentCard, 'etiqueta_automatica', e.target.value)}
                                placeholder='Ej: "Soy un/a procrastinador/a", "No tengo disciplina"'
                                className="senda-input"
                                data-testid={`etiqueta-input-${currentCard}`}
                            />
                        </div>

                        {/* Categoría real */}
                        <div className="space-y-3">
                            <Label className="senda-label">¿Cuál crees que fue la causa real? (selecciona una o más)</Label>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIAS.map((cat) => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        onClick={() => toggleCategoria(currentCard, cat.id)}
                                        className={`senda-chip ${
                                            currentCategorias.includes(cat.id)
                                                ? 'senda-chip-selected'
                                                : 'senda-chip-unselected'
                                        }`}
                                        data-testid={`categoria-chip-${cat.id}-${currentCard}`}
                                    >
                                        {currentCategorias.includes(cat.id) && (
                                            <Check className="h-3 w-3 mr-1" />
                                        )}
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Nivel de certeza */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué tan seguro/a estás de tu análisis?</Label>
                            <Select 
                                key={`certeza-${currentCard}`}
                                defaultValue={currentSit.nivel_certeza || undefined}
                                onValueChange={(value) => updateSituacion(currentCard, 'nivel_certeza', value)}
                            >
                                <SelectTrigger className="senda-input" data-testid={`certeza-select-${currentCard}`}>
                                    <SelectValue placeholder="Selecciona tu nivel de certeza" />
                                </SelectTrigger>
                                <SelectContent>
                                    {CERTEZA_NIVELES.map((nivel) => (
                                        <SelectItem key={nivel.value} value={nivel.value}>
                                            {nivel.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Validation message */}
                        {!hasEnoughCategories && completedCount >= 3 && (
                            <div className="p-4 bg-[#E8B27A]/10 rounded-lg border border-[#E8B27A]/20 flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-[#E8B27A] shrink-0 mt-0.5" />
                                <p className="text-sm text-[#E8B27A]">
                                    Necesitas identificar al menos 2 categorías distintas entre todas tus situaciones para completar el módulo.
                                </p>
                            </div>
                        )}

                        {/* Navigation */}
                        <div className="flex items-center justify-between pt-6 border-t border-[#EAE8E3]">
                            <Button
                                variant="ghost"
                                onClick={() => setCurrentCard(prev => Math.max(0, prev - 1))}
                                disabled={currentCard === 0}
                                className="senda-btn-ghost"
                                data-testid="prev-card-btn"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Anterior
                            </Button>
                            
                            {currentCard < 2 ? (
                                <Button
                                    onClick={() => setCurrentCard(prev => Math.min(2, prev + 1))}
                                    className="senda-btn-primary"
                                    data-testid="next-card-btn"
                                >
                                    Siguiente
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit || loading}
                                    className="senda-btn-primary"
                                    data-testid="submit-module-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            Completar módulo
                                            <Check className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
